/**
 * Attentive back-in-stock waitlist.
 *
 * Attentive's tag owns the CTA end to end: we render the trigger div and the
 * eligibility map, fire a productView, and the tag injects its own button and
 * records the enrollment. Never add our own email field — `__attentive.trigger`
 * opens a generic sign-up unit that creates a subscriber with no enrollment on
 * the variant, so the restock alert never fires.
 *
 * Testing requires a US IP: the unit is geo-targeted, and from elsewhere
 * Attentive returns no creatives and no button appears.
 */
(function () {
  const SIZE_OPTION_PATTERN = /size/i;
  const ATTENTIVE_POLL_MS = 250;
  const ATTENTIVE_TIMEOUT_MS = 5000;

  // Attentive leaves its containers on the page at a small idle size, so an
  // open unit is judged by area, not presence.
  const CREATIVE_SELECTOR =
    '#attentive_creative, [id^="attentive_creative"], #attentive_overlay, iframe[src*="attn.tv"]';
  const CREATIVE_MIN_AREA = 200 * 200;
  const CREATIVE_GROWTH_FACTOR = 3;
  const CREATIVE_POLL_MS = 150;
  const CREATIVE_TIMEOUT_MS = 2000;

  const VARIANT_INPUT_SELECTOR =
    'product-form-component form[data-type="add-to-cart-form"] input[name="id"]';

  /**
   * @returns {Record<string, string>}
   */
  function getVariantSizeMap() {
    const node = document.querySelector('[data-fv-waitlist-variant-sizes]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  /**
   * @param {HTMLElement} root
   * @returns {string}
   */
  function getDrawerSelectedSize(root) {
    const select = root.querySelector('select[data-fv-waitlist-sizes]');
    return select && select.value ? String(select.value).trim() : '';
  }

  /**
   * @param {HTMLElement} root
   * @returns {string}
   */
  function getSelectedSize(root) {
    const drawerSize = getDrawerSelectedSize(root);
    if (drawerSize) return drawerSize;

    const variantInput = document.querySelector(VARIANT_INPUT_SELECTOR);
    const variantId = variantInput && variantInput.value;
    const sizeMap = getVariantSizeMap();
    if (variantId && sizeMap[variantId]) return String(sizeMap[variantId]).trim();

    const fieldsets = document.querySelectorAll('fieldset.variant-option');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (!SIZE_OPTION_PATTERN.test((legend?.textContent || '').trim())) continue;
      const checked = fieldset.querySelector('input:checked');
      if (checked?.value) return checked.value.trim();
    }

    return root.dataset.sizeValue || root.dataset.sizeFallback || '';
  }

  /**
   * Point the product form at the drawer's size, so Attentive attributes the
   * enrollment to that variant.
   * @param {HTMLElement} root
   */
  function syncProductFormVariant(root) {
    const select = root.querySelector('select[data-fv-waitlist-sizes]');
    const checked = select && select.selectedOptions[0];
    const variantId = checked && checked.dataset.variantId;
    if (!variantId) return;

    setFormVariant(variantId);
  }

  /**
   * Point the product form back at the variant the PDP picker is showing, so
   * closing the drawer doesn't leave Add to cart (or the URL) on the sold-out
   * size picked inside it.
   */
  function syncProductFormToPicker() {
    const variantInput = document.querySelector(VARIANT_INPUT_SELECTOR);
    if (!(variantInput instanceof HTMLInputElement)) return;

    const scope = variantInput.closest('.shopify-section') || document;
    const node = scope.querySelector('variant-picker script[type="application/json"]');
    let variantId = '';
    try {
      const variant = JSON.parse((node && node.textContent) || 'null');
      variantId = variant && variant.id ? String(variant.id) : '';
    } catch (error) {
      return;
    }
    if (!variantId || variantInput.value === variantId) return;

    setFormVariant(variantId);
    fireProductView();
  }

  /**
   * @param {string} variantId
   */
  function setFormVariant(variantId) {
    const variantInput = document.querySelector(VARIANT_INPUT_SELECTOR);
    if (variantInput instanceof HTMLInputElement) variantInput.value = variantId;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
  }

  /**
   * @param {string} template
   * @param {string} size
   * @returns {string}
   */
  function applySize(template, size) {
    return (template || '').replace(/__SIZE__/g, size).replace(/\{\{\s*size\s*\}\}/g, size);
  }

  /**
   * Decode entities from Liquid `| escape` data attributes.
   * @param {string} value
   * @returns {string}
   */
  function decodeHtmlEntities(value) {
    if (!value || !/[&]/.test(value)) return value || '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} [size]
   */
  function updateCopy(root, size) {
    const sizeLabel = root.querySelector('[data-fv-waitlist-size]');
    const body = root.querySelector('[data-fv-waitlist-body]');
    const similarHeading = root.querySelector('[data-fv-waitlist-similar-heading]');
    const displaySize = size || '';

    if (sizeLabel) {
      sizeLabel.textContent = applySize(
        decodeHtmlEntities(root.dataset.sizeLabelTemplate || 'Size: __SIZE__'),
        displaySize
      );
      sizeLabel.hidden = !displaySize;
    }

    if (body) {
      body.textContent = displaySize
        ? applySize(
            decodeHtmlEntities(
              root.dataset.bodyTemplate ||
                'Size __SIZE__ is out of stock, be the first to know when your size is back in stock.'
            ),
            displaySize
          )
        : decodeHtmlEntities(
            root.dataset.bodyTemplateProduct ||
              'This product is out of stock. Be the first to know when it is back.'
          );
    }

    if (similarHeading) {
      similarHeading.textContent = displaySize
        ? applySize(
            decodeHtmlEntities(root.dataset.similarTemplate || 'Similar products in size __SIZE__'),
            displaySize
          )
        : decodeHtmlEntities(root.dataset.similarTemplateProduct || 'Similar products');
    }

    root.dataset.sizeValue = displaySize;
  }

  /**
   * @param {HTMLElement} root
   */
  function syncDrawerSizeFromPdp(root) {
    const sizeMap = getVariantSizeMap();
    const variantInput = document.querySelector(VARIANT_INPUT_SELECTOR);
    const variantId = variantInput && variantInput.value;
    const pdpSize = variantId && sizeMap[variantId] ? String(sizeMap[variantId]).trim() : '';
    if (!pdpSize) return;

    const select = root.querySelector('select[data-fv-waitlist-sizes]');
    if (!select) return;

    // In-stock sizes are disabled (nothing to wait for), so the select stays
    // on its placeholder until the shopper picks a sold-out size.
    for (const option of select.options) {
      if (option.value === pdpSize && !option.disabled) {
        select.value = pdpSize;
        break;
      }
    }
  }

  /**
   * @param {string} raw
   * @returns {Record<string, string>}
   */
  function parseSizeVariantMap(raw) {
    const map = {};
    String(raw || '')
      .split(';')
      .forEach((pair) => {
        const idx = pair.indexOf(':');
        if (idx === -1) return;
        const size = pair.slice(0, idx).trim();
        const id = pair.slice(idx + 1).trim();
        if (size && id) map[size] = id;
      });
    return map;
  }

  /**
   * Show only recommendation cards with the selected size in stock.
   * @param {HTMLElement} root
   */
  function filterRecommendationsBySize(root) {
    const size = getSelectedSize(root);
    const host = root.querySelector('product-recommendations');
    const similarHeading = root.querySelector('[data-fv-waitlist-similar-heading]');
    if (!host) return;

    const cards = host.querySelectorAll('[data-fv-waitlist-rec-card]');
    if (!cards.length) {
      host.classList.add('hidden');
      if (similarHeading) similarHeading.hidden = true;
      return;
    }

    const maxVisible = 6;
    let visible = 0;

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;
      const sizes = String(card.dataset.inStockSizes || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean);

      if (size && sizes.includes(size) && visible < maxVisible) {
        visible += 1;
        card.hidden = false;

        const variantId = parseSizeVariantMap(card.dataset.sizeVariantMap)[size];
        const baseUrl = card.dataset.productUrl || '';
        let href = baseUrl;
        if (variantId && baseUrl) {
          try {
            const url = new URL(baseUrl, window.location.origin);
            url.searchParams.set('variant', variantId);
            href = `${url.pathname}${url.search}`;
          } catch (error) {
            href = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}variant=${variantId}`;
          }
        }

        card.querySelectorAll('[data-fv-waitlist-rec-link]').forEach((link) => {
          if (href) link.setAttribute('href', href);
        });
      } else {
        card.hidden = true;
      }
    });

    host.classList.toggle('hidden', visible === 0);
    if (similarHeading) similarHeading.hidden = visible === 0 || !size;
  }

  /**
   * @param {HTMLElement} root
   */
  function refreshRecommendations(root) {
    const host = root.querySelector('product-recommendations');
    if (!host) return;

    host.classList.remove('hidden');
    delete host.dataset.error;

    const applyFilter = () => filterRecommendationsBySize(root);

    if (typeof host.load === 'function') {
      const observer = new MutationObserver(() => {
        if (host.querySelector('[data-fv-waitlist-rec-card]')) {
          observer.disconnect();
          applyFilter();
        }
      });
      observer.observe(host, { childList: true, subtree: true });
      host.load();
      // Cover the cached/already-rendered case.
      requestAnimationFrame(applyFilter);
      return;
    }

    host.dataset.recommendationsPerformed = 'false';
    requestAnimationFrame(applyFilter);
  }

  /**
   * @returns {{productId: string, currency: string, defaultVariantId: string, variants: Record<string, any>} | null}
   */
  function getProductViewData() {
    const node = document.querySelector('[data-fv-waitlist-productview]');
    if (!node) return null;
    try {
      const data = JSON.parse(node.textContent || 'null');
      return data && data.variants ? data : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * The hidden `id` input is the same signal Attentive's tag polls, so reading
   * it keeps our productView on the variant it checks eligibility for. A
   * sold-out PDP may render no usable input and a visitor may arrive without
   * `?variant=`, so fall back to the rendered default rather than send nothing.
   * @param {{defaultVariantId?: string} | null} data
   * @returns {string}
   */
  function getCurrentVariantId(data) {
    const input = document.querySelector(VARIANT_INPUT_SELECTOR);
    if (input && input.value) return String(input.value);

    try {
      const fromUrl = new URL(window.location.href).searchParams.get('variant');
      if (fromUrl) return fromUrl;
    } catch (error) {
      /* fall through */
    }

    return data && data.defaultVariantId ? String(data.defaultVariantId) : '';
  }

  /**
   * `image_url` yields a protocol-relative path; Attentive wants absolute.
   * @param {string} url
   * @returns {string}
   */
  function absoluteImageUrl(url) {
    const value = String(url || '');
    return value.startsWith('//') ? `https:${value}` : value;
  }

  /**
   * @returns {{productView: Function} | null}
   */
  function getAttentiveAnalytics() {
    const analytics = window.attentive && window.attentive.analytics;
    return analytics && typeof analytics.productView === 'function' ? analytics : null;
  }

  /**
   * attn.js loads async, so the first productView usually beats the tag.
   * @returns {Promise<{productView: Function} | null>}
   */
  function waitForAttentiveAnalytics() {
    const ready = getAttentiveAnalytics();
    if (ready) return Promise.resolve(ready);

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const timer = setInterval(() => {
        const analytics = getAttentiveAnalytics();
        if (analytics) {
          clearInterval(timer);
          resolve(analytics);
          return;
        }
        if (performance.now() - startedAt >= ATTENTIVE_TIMEOUT_MS) {
          clearInterval(timer);
          resolve(null);
        }
      }, ATTENTIVE_POLL_MS);
    });
  }

  /** @type {string} */
  let lastProductViewKey = '';

  /**
   * Tell Attentive which product and variant is being viewed. This is what
   * makes it stamp the trigger and offer the waitlist.
   * @returns {Promise<void>}
   */
  async function fireProductView() {
    const data = getProductViewData();
    if (!data) return;

    const variantId = getCurrentVariantId(data);
    const variant = variantId ? data.variants[variantId] : null;
    if (!variant) return;

    const key = `${data.productId}:${variant.productVariantId}`;
    if (key === lastProductViewKey) return;
    // Claim the key before awaiting, so a burst of variant:update events can't
    // queue duplicate sends.
    lastProductViewKey = key;

    const analytics = await waitForAttentiveAnalytics();
    if (!analytics) {
      if (lastProductViewKey === key) lastProductViewKey = '';
      return;
    }

    const cents = Number(variant.priceCents);

    try {
      analytics.productView({
        items: [
          {
            productId: String(data.productId),
            productVariantId: String(variant.productVariantId),
            name: variant.name || '',
            productImage: absoluteImageUrl(variant.productImage),
            price: {
              value: (Number.isFinite(cents) ? cents / 100 : 0).toFixed(2),
              currency: data.currency || 'USD',
            },
          },
        ],
      });
    } catch (error) {
      console.warn('[fv-waitlist] Attentive productView failed', error);
    }
  }

  /**
   * Bound once per document: variant selection morphs the section, which would
   * otherwise stack a duplicate listener on every re-bind.
   */
  function bindProductView() {
    if (document.documentElement.dataset.fvWaitlistProductViewBound === 'true') return;
    document.documentElement.dataset.fvWaitlistProductViewBound = 'true';

    fireProductView();

    document.addEventListener('variant:update', () => {
      // Wait for the morphed markup so we read the new variant, not the old.
      requestAnimationFrame(fireProductView);
    });
  }

  function prepareAttentiveTrigger() {
    const trigger = document.getElementById('attn-waitlist-trigger');
    if (!trigger) return;
    trigger.style.removeProperty('display');
    trigger.hidden = false;
  }

  /**
   * Largest on-screen area among Attentive's containers.
   * @returns {number}
   */
  function creativeArea() {
    let largest = 0;
    document.querySelectorAll(CREATIVE_SELECTOR).forEach((node) => {
      const rect = node.getBoundingClientRect();
      largest = Math.max(largest, rect.width * rect.height);
    });
    return largest;
  }

  /**
   * @param {number} baseline area before the click
   * @returns {boolean}
   */
  function creativeOpened(baseline) {
    const area = creativeArea();
    if (area >= CREATIVE_MIN_AREA) return true;
    return baseline > 0 && area >= baseline * CREATIVE_GROWTH_FACTOR;
  }

  /**
   * @param {number} baseline
   * @returns {Promise<boolean>}
   */
  function waitForCreative(baseline) {
    if (creativeOpened(baseline)) return Promise.resolve(true);

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const timer = setInterval(() => {
        if (creativeOpened(baseline)) {
          clearInterval(timer);
          resolve(true);
          return;
        }
        if (performance.now() - startedAt >= CREATIVE_TIMEOUT_MS) {
          clearInterval(timer);
          resolve(false);
        }
      }, CREATIVE_POLL_MS);
    });
  }

  function closeWaitlistDrawer() {
    const drawer = document.getElementById('fv-waitlist-drawer');
    if (!drawer) return;

    if (typeof drawer.closeDialog === 'function') {
      drawer.closeDialog();
      return;
    }
    const dialog = drawer.querySelector('dialog');
    if (dialog && dialog.open) dialog.close();
  }

  /**
   * Get the drawer out of the way of Attentive's popup.
   *
   * Our drawer is a `<dialog>` opened with `showModal()`, so it lives in the
   * browser's top layer and no z-index on their normal-DOM popup can rise above
   * it. Closing is safe: the enrollment POST fires from the button's own
   * onclick, before the popup opens. We wait for the popup rather than closing
   * on click because an already-subscribed visitor gets no popup — the tag just
   * flips the button to a confirmation state in place.
   */
  function bindAttentiveCtaHandoff() {
    if (document.documentElement.dataset.fvWaitlistCtaHandoffBound === 'true') return;
    document.documentElement.dataset.fvWaitlistCtaHandoffBound = 'true';

    // Delegated: Attentive injects the button late and can re-inject it.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('#attn-waitlist-trigger')) return;

        const baseline = creativeArea();
        waitForCreative(baseline).then((opened) => {
          if (opened) closeWaitlistDrawer();
        });
      },
      true
    );
  }

  /**
   * @returns {boolean}
   */
  function openWaitlistDrawer() {
    const drawer = document.getElementById('fv-waitlist-drawer');
    if (!drawer) return false;

    let opened = false;

    if (typeof drawer.showDialog === 'function') {
      drawer.showDialog();
      opened = true;
    } else {
      const dialog = drawer.querySelector('dialog');
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
        opened = true;
      }
    }

    if (opened) {
      const root = drawer.closest('[data-fv-waitlist]') || drawer;
      if (root instanceof HTMLElement) {
        syncDrawerSizeFromPdp(root);
        updateCopy(root, getSelectedSize(root));
      }
    }

    return opened;
  }

  /**
   * Sort the size options ascending by numeric value (Shopify returns them in
   * option order, e.g. 10 before 5). Non-numeric values keep their relative
   * order after the numeric ones; the placeholder stays first.
   * @param {HTMLElement} root
   */
  function sortSizeOptions(root) {
    const select = root.querySelector('select[data-fv-waitlist-sizes]');
    if (!select) return;

    const options = Array.from(select.querySelectorAll('option[data-fv-waitlist-size-option]'));
    options
      .map((option, index) => {
        const num = parseFloat(option.value);
        return { option, num: isNaN(num) ? Infinity : num, index };
      })
      .sort((a, b) => a.num - b.num || a.index - b.index)
      .forEach((item) => select.appendChild(item.option));
  }

  /**
   * @param {HTMLElement} root
   */
  function bind(root) {
    if (root.dataset.fvBound === 'true') return;
    root.dataset.fvBound = 'true';

    sortSizeOptions(root);

    syncDrawerSizeFromPdp(root);
    updateCopy(root, getSelectedSize(root));

    const dialog = root.querySelector('dialog');
    if (dialog) {
      const observer = new MutationObserver(() => {
        if (!dialog.hasAttribute('open')) {
          syncProductFormToPicker();
          return;
        }
        syncDrawerSizeFromPdp(root);
        // A size picked on a previous open is still selected here; re-point the
        // form at it and re-announce it so Attentive re-injects its button.
        syncProductFormVariant(root);
        lastProductViewKey = '';
        fireProductView();
        updateCopy(root, getSelectedSize(root));
        prepareAttentiveTrigger();
        refreshRecommendations(root);
      });
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }

    const sizeOptions = root.querySelector('[data-fv-waitlist-sizes]');
    if (sizeOptions) {
      sizeOptions.addEventListener('change', () => {
        syncProductFormVariant(root);
        fireProductView();
        updateCopy(root, getSelectedSize(root));
        const host = root.querySelector('product-recommendations');
        if (host && host.querySelector('[data-fv-waitlist-rec-card]')) {
          filterRecommendationsBySize(root);
        } else {
          refreshRecommendations(root);
        }
      });
    }
  }

  /**
   * Registered once per document and re-queries the live root, because `bind`
   * re-runs after every section morph and the root it closed over is detached.
   */
  function bindVariantCopySync() {
    if (document.documentElement.dataset.fvWaitlistCopySyncBound === 'true') return;
    document.documentElement.dataset.fvWaitlistCopySyncBound = 'true';

    document.addEventListener('variant:update', () => {
      document.querySelectorAll('[data-fv-waitlist]').forEach((root) => {
        if (!(root instanceof HTMLElement)) return;
        syncDrawerSizeFromPdp(root);
        updateCopy(root, getSelectedSize(root));
      });
    });
  }

  function bindOpenButtons() {
    if (document.documentElement.dataset.fvWaitlistOpenBound === 'true') return;
    document.documentElement.dataset.fvWaitlistOpenBound = 'true';

    // Capture-phase fallback so the drawer opens even if theme on:click
    // delegation misses the button after a variant morph.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('.fv-waitlist-open') && !target.closest('[data-fv-waitlist-open-oos]')) return;

        event.preventDefault();
        openWaitlistDrawer();
      },
      true
    );
  }

  function init() {
    document.querySelectorAll('[data-fv-waitlist]').forEach(bind);
    bindOpenButtons();
    bindProductView();
    bindVariantCopySync();
    bindAttentiveCtaHandoff();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
